import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { useUnexpectedAppointments } from '@/hooks/useAppointmentTypes/useAppointmentTypes';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import PersonContext from '@/pages/Person/PersonContext';
import AppointmentsRepository from '@/repositories/AppointmentsRepository';
import { Phase } from '@/types/phase';
import Accordion from '@/ui/components/Accordion';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading';
import IconItem from '@/ui/components/IconItem';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import { healthieLink } from '@/utils/healthieLinks';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const UnexpectedAppointments = ({ phase }: { phase: Phase }) => {
  const { id: phaseId } = phase;
  const { navigateToAppointment } = useNavigateToPage();
  const { person } = useContext(PersonContext);
  const { host, addMessage } = useContext(AppContext);

  const {
    data: unexpectedAppointments,
    isPending: isPendingUnexpectedAppointments,
    refetch: refetchUnexpectedAppointments
  } = useUnexpectedAppointments(phaseId);

  const appointmentsRepository = AppointmentsRepository();

  const cancelAllOverscheduled = useMutation({
    mutationFn: async (appointmentIds: string[]) => {
      return await appointmentsRepository.cancelAppointments(appointmentIds);
    },
    onSuccess: (data) => {
      addMessage({ level: 'success', text: `Cancelled ${data.length} overscheduled appointments` });
      refetchUnexpectedAppointments();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  if (isPendingUnexpectedAppointments) {
    return <LoadingSkeleton />;
  }

  if (!unexpectedAppointments?.length) {
    return null;
  }

  return (
    <div className="mb-4">
      <Accordion
        buttonText={
          <span className="text-base">
            <IconItem checked={false}>Unexpected Appointments</IconItem>
          </span>
        }
        buttonColor={Color.RED}
        additionalButtonContent={'This member has unexpected appointments scheduled'}
        defaultOpen={false}>
        {unexpectedAppointments.map((appointmentType) => {
          return (
            <Card
              key={appointmentType.appointment_type_id}
              topBar={{
                bgColor: Color.GRAY,
                content: <Heading value={3} text={appointmentType.appointment_type_name} />
              }}>
              <Heading value={4} text="Appointments" />
              <ul>
                {appointmentType.unexpected_attendances.map((appointment) => {
                  const startDate = new Date(appointment.start_at);
                  const formattedDate = startDate.toISOString().slice(0, 10).replace(/-/g, '');
                  const apptHref = `${healthieLink({ host })}/organization?rel_appt_id=${
                    appointment.appointment_healthie_id
                  }&rel_appt_date=${formattedDate}`;
                  return (
                    <li key={appointment.id} className="flex items-center">
                      <div className="flex items-center justify-between">
                        <button
                          className="text-blue-600 underline"
                          onClick={() =>
                            navigateToAppointment(person.id, appointment.appointment_id)
                          }>
                          <DualTime dateTime={new Date(appointment.start_at)} showWeekday={true} />
                        </button>
                      </div>
                      <a className="ml-3" href={apptHref} target="_blank" rel="noreferrer">
                        <span className="sr-only">Healthie link</span>
                        <FontAwesomeIcon icon={faExternalLink} />
                      </a>
                    </li>
                  );
                })}
              </ul>
              <Button
                className="mt-3"
                color={Color.RED}
                size="sm"
                disabled={cancelAllOverscheduled.isPending}
                onClick={() => {
                  const appointmentIds = appointmentType.unexpected_attendances.map(
                    (appt) => appt.id
                  );
                  cancelAllOverscheduled.mutate(appointmentIds);
                }}>
                Cancel all
              </Button>
            </Card>
          );
        })}
      </Accordion>
    </div>
  );
};
export default UnexpectedAppointments;
